import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { scrollToPlanner } from "@/lib/scrollToPlanner";

const eur = (n: number) =>
  new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const num = (n: number) => new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 0 }).format(n);
const pct = (n: number) => new Intl.NumberFormat("nl-NL", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n) + "%";

const useCountUp = (target: number, duration = 350) => {
  const [value, setValue] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    const start = performance.now();
    const initial = from.current;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const v = initial + (target - initial) * (1 - Math.pow(1 - p, 3));
      setValue(v);
      from.current = v;
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
};

const sliderCls =
  "py-2 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:bg-background [&_[role=slider]]:border-2 [&_[role=slider]]:border-accent [&>span:first-child]:h-1.5 [&>span:first-child>span]:bg-accent";

const CroCalculator = () => {
  const [conv, setConv] = useState(2);
  const [aov, setAov] = useState(75);
  const [sessions, setSessions] = useState(20000);
  const [lift, setLift] = useState(20);

  const revenue = sessions * (conv / 100) * aov;
  const extra = revenue * (lift / 100);
  const animRevenue = useCountUp(revenue);
  const animExtra = useCountUp(extra);

  const rows = [
    { label: "Conversiepercentage", value: pct(conv), v: conv, set: setConv, min: 0.5, max: 8, step: 0.1 },
    { label: "Gemiddelde bestelwaarde", value: eur(aov), v: aov, set: setAov, min: 20, max: 300, step: 5 },
    { label: "Maandelijkse sessies", value: num(sessions), v: sessions, set: setSessions, min: 1000, max: 150000, step: 1000 },
  ];

  return (
    <section className="bg-primary w-full py-16 md:py-24">
      <div className="max-w-[1400px] mx-auto px-5">
        <div className="text-center max-w-[640px] mx-auto mb-10 md:mb-12">
          <h2 className="text-3xl md:text-[2.6rem] font-semibold tracking-tight leading-[1.15] text-primary-foreground mb-4">
            Meer omzet uit <span className="accent-serif text-accent">hetzelfde verkeer</span>
          </h2>
          <p className="text-primary-foreground/70 text-sm md:text-base leading-relaxed">
            Kleine verbeteringen in conversie en orderwaarde kunnen een groot verschil maken. Bereken wat dat voor jouw webshop kan betekenen.
          </p>
        </div>

        <div className="max-w-[860px] mx-auto rounded-3xl overflow-hidden grid grid-cols-1 md:grid-cols-2 shadow-2xl">
          <div className="bg-background p-6 md:p-8 space-y-6">
            {rows.map((r) => (
              <div key={r.label}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-muted-foreground">{r.label}</span>
                  <span className="text-sm font-semibold text-primary tabular-nums">{r.value}</span>
                </div>
                <Slider
                  value={[r.v]}
                  onValueChange={(v) => r.set(v[0])}
                  min={r.min}
                  max={r.max}
                  step={r.step}
                  aria-label={r.label}
                  className={sliderCls}
                />
              </div>
            ))}
            <div className="pt-4 border-t border-border flex justify-between items-baseline gap-3">
              <span className="text-sm md:text-base font-medium text-primary">Geschatte maandelijkse omzet</span>
              <span className="text-xl md:text-2xl font-semibold text-primary tabular-nums whitespace-nowrap">
                {eur(animRevenue)}<span className="text-sm text-muted-foreground">/m</span>
              </span>
            </div>
          </div>

          <div className="bg-cream p-6 md:p-8 flex flex-col">
            <h3 className="text-lg md:text-xl font-semibold text-primary mb-4">Kies een groeiscenario</h3>
            <div className="grid grid-cols-3 gap-2 mb-4" role="radiogroup" aria-label="Groeiscenario">
              {[10, 20, 30].map((p) => (
                <button
                  key={p}
                  role="radio"
                  aria-checked={lift === p}
                  onClick={() => setLift(p)}
                  className={`h-11 rounded-xl text-sm font-semibold transition-colors ${
                    lift === p
                      ? "bg-accent text-accent-foreground"
                      : "bg-background border border-border text-muted-foreground hover:text-primary"
                  }`}
                >
                  +{p}%
                </button>
              ))}
            </div>
            <div className="flex-1 flex flex-col items-center justify-center text-center rounded-2xl bg-background border border-border p-6">
              <span className="text-xs text-muted-foreground mb-2">Wat deze groei kan betekenen:</span>
              <span className="text-4xl md:text-5xl font-semibold text-primary tabular-nums">{eur(animExtra)}</span>
              <span className="text-xs uppercase tracking-wider text-muted-foreground mt-2">extra omzet per maand</span>
            </div>
          </div>
        </div>

        <p className="text-center text-sm md:text-base font-medium text-primary-foreground max-w-[560px] mx-auto mt-5">
          Wij garanderen een minimale groei van +10%. Zitten wij eronder, dan betaal je niks.
        </p>

        <div className="text-center mt-10">
          <button
            onClick={scrollToPlanner}
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold h-12 px-8 rounded-full text-base shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)]"
          >
            Plan een gratis strategiesessie <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default CroCalculator;
